import { useState, type MouseEvent, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type {
  DeviceAlertSeverity,
  DeviceListItem,
} from '@kirin/api-client/contracts/device-inventory';
import { AgentUpdateStateBadge } from '@/components/shared/AgentUpdateStateBadge';
import {
  isAgentVersionAtLeast,
  showsUpdateAvailableWith,
} from '@/components/shared/agentVersion';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import type { TimestampFormat } from '@/contexts/TimestampFormatContext';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { PersonAvatar, SharedBadge } from './badges';
import { deviceDisplayName, personLabel } from './deviceDisplay';
import { buildEndpointAlertsUrl, buildEndpointPagePath } from './links';

/** Mockup 1: the endpoint name with the Shared badge inline. */
export function EndpointNameCell({
  row,
  organizationId,
}: {
  row: DeviceListItem;
  organizationId: string;
}): ReactElement {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Link
        to={buildEndpointPagePath(organizationId, row.id)}
        onClick={e => e.stopPropagation()}
        className="truncate font-semibold text-foreground underline-offset-2 hover:underline"
      >
        {deviceDisplayName(row)}
      </Link>
      {row.shared && <SharedBadge shared />}
    </div>
  );
}

export function OsCell({ row }: { row: DeviceListItem }): ReactElement {
  return (
    <span className="truncate" title={row.osString ?? undefined}>
      {row.osString ?? '—'}
    </span>
  );
}

const PILL =
  'inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-semibold';

/**
 * Mockup 1: a red version pill when the agent is older than the latest
 * release, plus an amber "Update available" pill on the same rule as the endpoint page
 * (`showsUpdateAvailableWith`: not while updating, failed, held or pinned); a
 * green "Up to date" pill only when it is known to be at or past it (the
 * tile's comparison), else the plain version (latest unknown, dev build,
 * unparseable). With automatic updates on, a state other than up to date
 * shows beside it.
 */
export function DeviceAgentVersionCell({
  row,
  latestAgentVersion,
  showUpdateState,
}: {
  row: DeviceListItem;
  latestAgentVersion: string | null;
  showUpdateState: boolean;
}): ReactElement {
  const shownState = showUpdateState ? row.updateState : null;
  const state = shownState && shownState !== 'up_to_date' ? shownState : null;
  const showUpdatePill = showsUpdateAvailableWith(shownState);
  if (!row.agentVersion) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {row.updateAvailable ? (
        <>
          <span
            className={cn(
              PILL,
              'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300'
            )}
          >
            {row.agentVersion}
          </span>
          {showUpdatePill && (
            <span
              className={cn(
                PILL,
                'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
              )}
            >
              Update available
            </span>
          )}
        </>
      ) : isAgentVersionAtLeast(row.agentVersion, latestAgentVersion) ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={cn(
                PILL,
                'cursor-help border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
              )}
            >
              Up to date
            </span>
          </TooltipTrigger>
          <TooltipContent>Agent v{row.agentVersion}</TooltipContent>
        </Tooltip>
      ) : (
        <span>{row.agentVersion}</span>
      )}
      {state && <AgentUpdateStateBadge state={state} />}
    </span>
  );
}

/** Mockup 1: an avatar stack and "N people" on a shared machine, else the one person. */
export function PeopleSeenCell({ row }: { row: DeviceListItem }): ReactElement {
  if (row.peopleSeenCount === 0 || row.peopleSeen.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  if (!row.shared) {
    const person = row.peopleSeen[0];
    const label = personLabel(person);
    return (
      <span className="flex min-w-0 items-center gap-2">
        <PersonAvatar
          label={label}
          seed={person.userId}
          muted={!person.name && !person.email}
        />
        <span className="truncate">{label}</span>
      </span>
    );
  }
  const names = row.peopleSeen.map(personLabel);
  return (
    <div className="flex flex-col gap-0.5" title={names.join(', ')}>
      <div className="flex -space-x-1.5">
        {row.peopleSeen.map((person, index) => (
          <PersonAvatar
            key={person.userId}
            label={names[index]}
            seed={person.userId}
            muted={!person.name && !person.email}
            className="size-6 text-[10px]"
          />
        ))}
      </div>
      <span className="text-xs text-muted-foreground">
        {row.peopleSeenCount} people
      </span>
    </div>
  );
}

/** Mockup 1: tool chips and "+N" for the rest; a dash while Shadow AI is off. */
export function AiToolsCell({ row }: { row: DeviceListItem }): ReactElement {
  if (row.aiToolCount === null || row.aiToolNames === null) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help text-muted-foreground">—</span>
        </TooltipTrigger>
        <TooltipContent>Shadow AI is off for your organization.</TooltipContent>
      </Tooltip>
    );
  }
  if (row.aiToolCount === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  const more = row.aiToolCount - row.aiToolNames.length;
  return (
    <span className="flex flex-wrap items-center gap-1">
      {row.aiToolNames.map(name => (
        <span
          key={name}
          className="whitespace-nowrap rounded-md border bg-muted/50 px-1.5 py-0.5 text-xs font-medium"
        >
          {name}
        </span>
      ))}
      {more > 0 && (
        <span className="rounded-md border bg-muted/50 px-1.5 py-0.5 text-xs font-medium">
          +{more}
        </span>
      )}
    </span>
  );
}

const SEVERITY_PILL: Record<DeviceAlertSeverity, string> = {
  critical:
    'border-red-300 bg-red-100 text-red-800 dark:border-red-800 dark:bg-red-900/50 dark:text-red-200',
  high: 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300',
  medium:
    'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  low: 'border-border bg-muted text-foreground',
};

/**
 * Alert count of the last 7 days as a pill coloured by the worst severity,
 * linking to the alerts. Plain (no link) while `linkToAlerts` is off: the
 * Alerts page drops the endpoint filter without `endpoint_identifier`. A
 * click builds the link's 7-day window from the click time, so a tab left
 * open does not widen it.
 */
export function DeviceAlertsCell({
  count,
  maxSeverity,
  machineIds,
  organizationId,
  userId,
  linkToAlerts,
}: {
  count: number;
  maxSeverity: DeviceAlertSeverity | null;
  machineIds: string[];
  organizationId: string;
  userId?: string;
  linkToAlerts: boolean;
}): ReactElement {
  const navigate = useNavigate();
  const [renderedAt] = useState(Date.now);
  const buildHref = (now: number): string =>
    buildEndpointAlertsUrl(organizationId, machineIds, now, { userId });
  const openAlerts = (e: MouseEvent<HTMLAnchorElement>): void => {
    e.stopPropagation();
    // A modified click (new tab or window) keeps the browser's own handling.
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    e.preventDefault();
    navigate(buildHref(Date.now()));
  };
  if (count === 0) {
    return <span className="text-muted-foreground">0</span>;
  }
  const pillClass = cn(
    PILL,
    'min-w-8 justify-center',
    maxSeverity ? SEVERITY_PILL[maxSeverity] : SEVERITY_PILL.low
  );
  if (!linkToAlerts) {
    return (
      <span data-severity={maxSeverity ?? undefined} className={pillClass}>
        {count}
      </span>
    );
  }
  return (
    <Link
      to={buildHref(renderedAt)}
      onClick={openAlerts}
      data-severity={maxSeverity ?? undefined}
      className={cn(pillClass, 'hover:underline')}
    >
      {count}
    </Link>
  );
}

const SEVERITY_LABELS: Record<DeviceAlertSeverity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

/** A severity pill (Mockups 2–3 alert rows); "Unknown" for an unrecognised one. */
export function SeverityPill({
  severity,
}: {
  severity: DeviceAlertSeverity | null;
}): ReactElement {
  return (
    <span
      data-severity={severity ?? 'unknown'}
      className={cn(
        PILL,
        severity === 'low' || severity === null
          ? 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-800 dark:bg-sky-900/30 dark:text-sky-300'
          : SEVERITY_PILL[severity]
      )}
    >
      {severity ? SEVERITY_LABELS[severity] : 'Unknown'}
    </span>
  );
}

export function LastSeenCell({
  value,
  format,
}: {
  value: string | null;
  format: TimestampFormat;
}): ReactElement {
  return (
    <span className="text-muted-foreground">
      {value ? (
        <RelativeTimeWithTooltip date={value} format={format} variant="fuzzy" />
      ) : (
        '—'
      )}
    </span>
  );
}
